/**
 * GrokLeads — "Dancing Shoes" workspace page.
 *
 * Visual spec: faithful recreation of the user-supplied screenshot
 * (2026-10-10). The underlying wiring (store, polling, atomic claim)
 * is unchanged from the previous ship — this pass is purely layout +
 * typography + proportions.
 *
 * Layout:
 *   • Oversized title row with square "sparkle" icon + Live pill +
 *     "How it works" link anchored right.
 *   • 3 summary cards with a prominent pastel icon circle on the LEFT
 *     and the big number stacked above the label.
 *   • Tab rail (All / New / Claimed by me / In Progress / Closed) +
 *     search + sort dropdown on the right.
 *   • Lead list rendered as individually-bordered rounded "card rows"
 *     with hairline column headers above. Selected row = tinted blue
 *     background + blue ring.
 *   • Right drawer: tighter than before, three dedicated cards
 *     (Contact / Loan Details / Documents), handoff summary card in
 *     mint, AI-collected + Documents side-by-side at the bottom.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Sparkles, Search, Phone as PhoneIcon, HelpCircle,
  Zap, PhoneCall, CheckCircle, ChevronDown, ChevronRight, Store,
} from 'lucide-react';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { formatPhone } from '../lib/utils';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/authStore';
import { grokLeadsStore } from '../lib/grokLeadsStore';
import GrokLeadDetailPanel from '../components/grok/GrokLeadDetailPanel';

const SORT_OPTIONS = [
  { key: 'newest', label: 'Newest first' },
  { key: 'oldest', label: 'Oldest first' },
  { key: 'hot',    label: 'Hot first' },
];

const TABS = [
  { key: 'new',      label: 'Waiting to accept' },
  { key: 'accepted', label: 'Accepted' },
];

const PRIORITY_WEIGHT = { hot: 0, warm: 1, cold: 2 };

const STATUS_CHIP = {
  new:         { dot: 'bg-neutral-400', label: 'New',         wrap: 'bg-neutral-100 text-neutral-700' },
  in_progress: { dot: 'bg-sky-500',     label: 'In Progress', wrap: 'bg-sky-50 text-sky-700' },
  closed:      { dot: 'bg-emerald-500', label: 'Closed',      wrap: 'bg-emerald-50 text-emerald-700' },
};

const PRIORITY_CHIP = {
  hot:  { dot: 'bg-red-500',    wrap: 'text-red-600',    label: 'Hot' },
  warm: { dot: 'bg-amber-500',  wrap: 'text-amber-700',  label: 'Warm' },
  cold: { dot: 'bg-sky-500',    wrap: 'text-sky-700',    label: 'Cold' },
};

function timeAgo(iso) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function fmtCurrency(n) {
  if (n == null) return '—';
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `$${v.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function initialsOf(name) {
  if (!name) return '??';
  return name.split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();
}

function PriorityPill({ priority }) {
  const p = PRIORITY_CHIP[priority] || PRIORITY_CHIP.warm;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${p.wrap}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${p.dot}`} />
      {p.label}
    </span>
  );
}

function StatusPill({ status }) {
  const s = STATUS_CHIP[status] || STATUS_CHIP.new;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${s.wrap}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

/**
 * Big KPI card — pastel icon circle on the left, metric stacked on the
 * right (number → label → one-line subtitle). The reference uses a
 * small pastel-blue chip for the "New Leads" lightning icon and a soft
 * mint chip for "In Progress" / "Closed".
 */
function KpiCard({ icon: Icon, iconBg, iconColor, value, label, sub, testId }) {
  return (
    <div
      className="flex-1 flex items-center gap-4 rounded-2xl border hairline bg-white dark:bg-neutral-950 px-5 py-4"
      data-testid={testId}
    >
      <div className={`h-11 w-11 rounded-full flex items-center justify-center shrink-0 ${iconBg}`}>
        <Icon className={`h-5 w-5 ${iconColor}`} />
      </div>
      <div className="min-w-0">
        <div className="text-[26px] leading-none font-semibold text-neutral-900 dark:text-white tabular-nums">
          {value}
        </div>
        <div className="text-[13px] text-neutral-900 dark:text-neutral-100 font-medium mt-1">{label}</div>
        <div className="text-[11.5px] text-neutral-500">{sub}</div>
      </div>
    </div>
  );
}

/** Loan-goal cell: two-line stack (top = goal, bottom = type/purpose). */
function loanGoal(l) {
  const top = l.borrowerGoal || l.loanPurpose || '—';
  // Compact bottom line: joins type + purpose like "Conventional refinance"
  const parts = [];
  if (l.loanType) parts.push(l.loanType);
  if (l.loanPurpose && l.loanPurpose !== top) parts.push(l.loanPurpose.toLowerCase());
  return { top, bottom: parts.length ? parts.join(' ') : null };
}

export default function GrokLeads() {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [snap, setSnap] = useState(grokLeadsStore.getSnapshot());
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('new');
  const [sort, setSort] = useState('newest');
  const [selected, setSelected] = useState(null);
  const [loFilter, setLoFilter] = useState('all');
  const isAdmin = me?.role === 'admin';

  useEffect(() => grokLeadsStore.subscribe(setSnap), []);

  const { leads, stats, loading, error } = snap;

  // Keep the detail panel in sync when the store refreshes the selected lead.
  useEffect(() => {
    if (!selected?.id) return;
    const fresh = leads.find((l) => l.id === selected.id);
    if (fresh && fresh !== selected) setSelected(fresh);
  }, [leads, selected]);

  // Backend already scopes the list: LOs get only leads their own bot
  // posted; admins get every LO's leads and can narrow by LO here.
  const loOptions = useMemo(() => {
    const m = new Map();
    leads.forEach((l) => { if (l.assignedTo) m.set(l.assignedTo, l.assignedToName || 'LO'); });
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [leads]);

  const scoped = useMemo(() => {
    if (!isAdmin || loFilter === 'all') return leads;
    if (loFilter === 'none') return leads.filter((l) => !l.assignedTo);
    return leads.filter((l) => l.assignedTo === loFilter);
  }, [leads, isAdmin, loFilter]);

  // Waiting = not yet accepted. Accepted leads live in Leads; the
  // Accepted tab is just a shortcut back to them.
  const tabCounts = useMemo(() => ({
    new:      scoped.filter((l) => !l.claimedBy && l.status === 'new').length,
    accepted: scoped.filter((l) => !!l.claimedBy).length,
    mine:     scoped.filter((l) => l.claimedBy === me?.id).length,
  }), [scoped, me?.id]);

  const filtered = useMemo(() => {
    let rows = tab === 'accepted'
      ? scoped.filter((l) => !!l.claimedBy)
      : scoped.filter((l) => !l.claimedBy && l.status === 'new');
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const qDigits = q.replace(/\D/g, '');
      rows = rows.filter((l) => (
        (l.name || '').toLowerCase().includes(q) ||
        (l.email || '').toLowerCase().includes(q) ||
        (qDigits && (l.phone || '').replace(/\D/g, '').includes(qDigits)) ||
        (l.propertyAddress || '').toLowerCase().includes(q)
      ));
    }
    const sorted = [...rows];
    if (sort === 'newest') {
      sorted.sort((a, b) => (b.enteredQueueAt || '').localeCompare(a.enteredQueueAt || ''));
    } else if (sort === 'oldest') {
      sorted.sort((a, b) => (a.enteredQueueAt || '').localeCompare(b.enteredQueueAt || ''));
    } else if (sort === 'hot') {
      sorted.sort((a, b) => {
        const w = (PRIORITY_WEIGHT[a.priority] ?? 9) - (PRIORITY_WEIGHT[b.priority] ?? 9);
        if (w !== 0) return w;
        return (b.enteredQueueAt || '').localeCompare(a.enteredQueueAt || '');
      });
    }
    return sorted;
  }, [scoped, tab, sort, search]);

  function openInLeads(e, lead) {
    e.stopPropagation();
    navigate(`/leads/${lead.id}`);
  }

  async function handleClaim(e, lead) {
    e.stopPropagation();
    try {
      const fresh = await grokLeadsStore.claim(lead.id);
      toast.success(`Accepted ${fresh.name || 'lead'}. It's now in your Leads tab.`);
      setSelected(fresh);
    } catch (err) {
      toast.error(err.message);
    }
  }

  return (
    <div className="flex-1 min-w-0 flex flex-col lg:flex-row bg-neutral-50/60 dark:bg-neutral-950 lg:items-start">
      {/* ── LEFT: queue ────────────────────────────────────────────── */}
      <div className="flex-1 min-w-0 px-6 lg:px-8 pt-6 pb-10 overflow-y-auto">
        {/* Page header — the sidebar's "LEADS MARKETPLACE" section
            header already names the parent concept, so the page title
            can be the specific feed ("Dancing Shoes") without the
            Marketplace wrapper. Keeps the h1 identifiable in bookmarks,
            tab titles, and keyboard search. */}
        <div className="flex items-start gap-4">
          <div className="h-14 w-14 rounded-2xl bg-emerald-500 flex items-center justify-center shrink-0 shadow-sm">
            <Sparkles className="h-6 w-6 text-white" strokeWidth={2.25} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-[36px] leading-tight font-bold tracking-tight text-neutral-900 dark:text-white">
                Dancing Shoes
              </h1>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Live
              </span>
            </div>
          </div>
          <button
            className="hidden md:inline-flex items-center gap-1.5 text-[12.5px] text-neutral-500 hover:text-neutral-900 shrink-0 mt-2"
            onClick={() => toast.info('Wingman qualifies borrowers through chat, then posts them here.')}
          >
            <HelpCircle className="h-3.5 w-3.5" />
            How it works
          </button>
        </div>

        {/* KPI cards */}
        <div className="mt-5 flex gap-4 flex-wrap md:flex-nowrap">
          <KpiCard
            icon={Zap}
            iconBg="bg-sky-50 border border-sky-100"
            iconColor="text-sky-500"
            value={tabCounts.new}
            label="Waiting to accept"
            sub="Sent by Wingman"
            testId="grok-stat-new"
          />
          <KpiCard
            icon={CheckCircle}
            iconBg="bg-emerald-50 border border-emerald-100"
            iconColor="text-emerald-600"
            value={tabCounts.mine}
            label="Accepted by me"
            sub="In your pipeline"
            testId="grok-stat-mine"
          />
        </div>

        {/* Tab rail + search */}
        <div className="mt-6 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1 flex-wrap">
            {TABS.map((t) => {
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`grok-tab-${t.key}`}
                  className={`inline-flex items-center gap-2 text-[13px] px-3 py-1.5 rounded-full transition-colors ${
                    active
                      ? 'bg-neutral-900 text-white'
                      : 'text-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-900'
                  }`}
                >
                  <span className="font-medium">{t.label}</span>
                  <span className={`tabular-nums text-[11px] font-semibold min-w-[18px] text-center px-1.5 py-px rounded ${
                    active ? 'bg-white/15 text-white' : 'bg-neutral-100 text-neutral-500 dark:bg-neutral-800'
                  }`}>
                    {tabCounts[t.key]}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="ml-auto flex items-center gap-2">
            {isAdmin && (
              <div className="relative">
                <select
                  value={loFilter}
                  onChange={(e) => setLoFilter(e.target.value)}
                  className="h-9 pl-3 pr-8 text-[12.5px] font-medium border hairline rounded-full bg-white dark:bg-neutral-950 appearance-none cursor-pointer"
                  data-testid="grok-lo-filter"
                >
                  <option value="all">All loan officers</option>
                  {loOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                  <option value="none">Unassigned (old shared key)</option>
                </select>
                <ChevronDown className="h-3.5 w-3.5 text-neutral-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            )}
            <div className="relative">
              <Search className="h-3.5 w-3.5 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, phone, address…"
                className="pl-8 w-[280px] h-9 text-[12.5px] rounded-full bg-white"
                data-testid="grok-search"
              />
            </div>
            <div className="relative">
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="h-9 pl-3 pr-8 text-[12.5px] font-medium border hairline rounded-full bg-white dark:bg-neutral-950 appearance-none cursor-pointer"
                data-testid="grok-sort"
              >
                {SORT_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
              <ChevronDown className="h-3.5 w-3.5 text-neutral-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Column headers */}
        <div className="mt-5 grid grid-cols-[1.6fr_1fr_0.5fr_1.5fr_1fr_0.95fr_0.95fr_1fr_1.3fr] gap-x-4 px-4 pb-2 text-[10.5px] uppercase tracking-widest text-neutral-400 font-medium">
          <div>Name</div>
          <div>Phone</div>
          <div>State</div>
          <div>Loan Goal</div>
          <div>Loan Amount</div>
          <div>Payment (est)</div>
          <div>Qualified</div>
          <div>Status</div>
          <div>Owner</div>
        </div>

        {/* Rows */}
        {error && (
          <div className="my-3 text-[12.5px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            Dancing Shoes queue returned an error: {error}
          </div>
        )}
        {!loading && !error && filtered.length === 0 && (
          <div className="my-10 flex flex-col items-center text-center text-neutral-400 py-12">
            <Sparkles className="h-6 w-6 mb-2" />
            <div className="text-[13px] font-medium text-neutral-600 dark:text-neutral-300">No leads here yet</div>
            <div className="text-[12px] max-w-sm mt-1">
              When Wingman qualifies a borrower, they&apos;ll appear here instantly.
            </div>
          </div>
        )}

        <div className="space-y-2.5" data-testid="grok-leads-table">
          {filtered.map((l) => {
            const g = loanGoal(l);
            const isSelected = selected?.id === l.id;
            const claimed = !!l.claimedBy;
            const amClaimant = me?.id && l.claimedBy === me.id;
            const canAccept = !l.assignedTo || l.assignedTo === me?.id;
            const ownerName = l.claimedByName || l.assignedToName;
            const ownerIsMe = (l.claimedBy || l.assignedTo) === me?.id;
            return (
              <div
                key={l.id}
                role="button"
                onClick={() => setSelected(l)}
                className={[
                  'grid grid-cols-[1.6fr_1fr_0.5fr_1.5fr_1fr_0.95fr_0.95fr_1fr_1.3fr] gap-x-4 items-center',
                  'px-4 py-3 rounded-xl text-[13px] cursor-pointer transition-all',
                  'border bg-white dark:bg-neutral-950',
                  isSelected
                    ? 'border-sky-200 bg-sky-50/60 ring-2 ring-sky-100'
                    : 'border-neutral-200/80 hover:border-neutral-300 hover:shadow-[0_1px_2px_rgba(0,0,0,0.03)]',
                ].join(' ')}
                data-testid={`grok-lead-row-${l.id}`}
              >
                {/* Name + avatar + priority pill */}
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-8 w-8 rounded-full bg-neutral-900 text-white text-[11px] font-semibold flex items-center justify-center shrink-0">
                    {initialsOf(l.name)}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-[13.5px] text-neutral-900 dark:text-white truncate leading-tight">
                      {l.name}
                    </div>
                    <div className="mt-0.5"><PriorityPill priority={l.priority} /></div>
                  </div>
                </div>
                <div className="tabular-nums text-neutral-700 dark:text-neutral-300 truncate">{formatPhone(l.phone)}</div>
                <div className="text-neutral-700 dark:text-neutral-300">{l.state || '—'}</div>
                <div className="min-w-0">
                  <div className="font-medium text-neutral-900 dark:text-white truncate leading-tight">{g.top}</div>
                  {g.bottom && <div className="text-[11.5px] text-neutral-500 truncate mt-0.5">{g.bottom}</div>}
                </div>
                <div className="tabular-nums text-neutral-900 dark:text-white font-semibold">{fmtCurrency(l.estLoanBalance)}</div>
                <div className="tabular-nums text-neutral-700 dark:text-neutral-300">
                  {l.estPayment ? `${fmtCurrency(l.estPayment)}/mo` : '—'}
                </div>
                <div className="text-neutral-500 whitespace-nowrap">{timeAgo(l.enteredQueueAt)}</div>
                <div><StatusPill status={l.status} /></div>
                {/* Owner cell: when claimed show avatar+name; when
                    unclaimed we skip the explicit "Unassigned" label
                    (removed Feb 10, 2026 — the Claim button already
                    makes the unassigned state obvious) and let the
                    button occupy the cell on its own. */}
                <div className="flex items-center justify-end gap-3 min-w-0">
                  {ownerName && !ownerIsMe ? (
                    <div className="flex items-center gap-2 min-w-[72px] flex-1" title={ownerName} data-testid={`grok-owner-${l.id}`}>
                      <div className="h-6 w-6 rounded-full bg-neutral-900 text-white text-[9.5px] font-semibold flex items-center justify-center shrink-0">
                        {initialsOf(ownerName)}
                      </div>
                      <span className="truncate text-[12px] text-neutral-700 dark:text-neutral-300">{ownerName}</span>
                    </div>
                  ) : claimed && amClaimant ? (
                    <span className="flex-1 text-[12px] text-neutral-700 dark:text-neutral-300">You</span>
                  ) : null}
                  {!claimed && canAccept && (
                    <Button
                      size="sm"
                      onClick={(e) => handleClaim(e, l)}
                      className="h-8 text-[11.5px] font-semibold px-3 rounded-lg bg-neutral-900 hover:bg-black text-white shrink-0"
                      data-testid={`grok-claim-btn-${l.id}`}
                    >
                      <PhoneIcon className="h-3 w-3 mr-1.5" />
                      Accept
                    </Button>
                  )}
                  {claimed && (amClaimant || isAdmin) && (
                    <button
                      onClick={(e) => openInLeads(e, l)}
                      data-testid={`grok-open-leads-btn-${l.id}`}
                      className="h-8 inline-flex items-center gap-1 text-[12px] font-medium px-2.5 rounded-lg border border-neutral-200 bg-white hover:bg-neutral-50 text-neutral-800 shrink-0 transition-colors"
                    >
                      In Leads
                      <ChevronRight className="h-3 w-3" />
                    </button>
                  )}
                  {!claimed && !canAccept && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelected(l); }}
                      data-testid={`grok-open-btn-${l.id}`}
                      className="h-8 inline-flex items-center gap-1 text-[12px] font-medium px-2.5 rounded-lg border border-neutral-200 bg-white hover:bg-neutral-50 text-neutral-800 shrink-0"
                    >
                      Open
                      <ChevronRight className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── RIGHT: detail panel (hidden until a row is picked, else always on xl+) ── */}
      <GrokLeadDetailPanel
        lead={selected}
        me={me}
        onClose={() => setSelected(null)}
        onClaimed={(fresh) => setSelected(fresh)}
      />
    </div>
  );
}
